import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import RateReviewOutlinedIcon from '@mui/icons-material/RateReviewOutlined';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import api from '../services/api';
import PageContainer from '../components/ui/PageContainer';
import SearchableSelect from '../components/SearchableSelect';
import PremiumSelect from '../components/PremiumSelect';
import AlertModal from '../components/AlertModal';
import { User, Cycle, AlertState } from '../types';
import { isCycleClosedForSubmissions } from '../utils/cycleFeedbackUi';

interface FeedbackFormData {
  developerId: string;
  cycleId: string;
  screensDeveloped: string;
  tasksCompleted: string;
  hoursWorked: string;
  averageComplexity: string;
  productivityScore: string;
  productivityJustification: string;
  productivityObservations: string;
  codeQualityScore: string;
  codeAdherence: 'sim' | 'parcialmente' | 'não';
  codeQualityObservations: string;
  validationScore: string;
  validationCompleteness: 'sim' | 'parcialmente' | 'não';
  validationObservations: string;
  strengths: string;
  improvements: string;
  nextActions: string;
}

const CreateFeedback: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);
  const [developers, setDevelopers] = useState<User[]>([]);
  const [cycles, setCycles] = useState<Cycle[]>([]);
  const [alert, setAlert] = useState<AlertState>({ isOpen: false, message: '', type: 'error' });
  const [formData, setFormData] = useState<FeedbackFormData>({
    developerId: '',
    cycleId: '',
    screensDeveloped: '',
    tasksCompleted: '',
    hoursWorked: '',
    averageComplexity: '',
    productivityScore: '3',
    productivityJustification: '',
    productivityObservations: '',
    codeQualityScore: '3',
    codeAdherence: 'sim',
    codeQualityObservations: '',
    validationScore: '3',
    validationCompleteness: 'sim',
    validationObservations: '',
    strengths: '',
    improvements: '',
    nextActions: '',
  });
  const yesPartialNoOptions = [
    { value: 'sim', label: 'Sim' },
    { value: 'parcialmente', label: 'Parcialmente' },
    { value: 'não', label: 'Não' },
  ];

  const normalizeNumberInput = (value: string): string => {
    if (value === '') return '';
    if (/^0+\d/.test(value)) return value.replace(/^0+/, '');
    return value;
  };

  const toNumber = (value: string): number => Number(value) || 0;

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async (): Promise<void> => {
    try {
      const [usersRes, cyclesRes] = await Promise.all([
        api.get<User[]>('/users/team'),
        api.get<Cycle[]>('/cycles'),
      ]);
      setDevelopers(usersRes.data);
      setCycles(
        cyclesRes.data
          .filter((c) => !isCycleClosedForSubmissions(c.deadlineDate))
          .sort((a, b) => Number(b.active) - Number(a.active) || new Date(b.startDate).getTime() - new Date(a.startDate).getTime()),
      );
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    }
  };

  const handleChange = (
    e:
      | React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
      | { target: { name: string; value: string } },
  ): void => {
    const { name, value } = e.target;
    const type = 'type' in e.target ? (e.target as HTMLInputElement).type : undefined;

    setFormData((prev) => ({
      ...prev,
      [name]: type === 'number' ? normalizeNumberInput(value) : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setLoading(true);

    try {
      await api.post('/feedbacks', {
        ...formData,
        screensDeveloped: toNumber(formData.screensDeveloped),
        tasksCompleted: toNumber(formData.tasksCompleted),
        hoursWorked: toNumber(formData.hoursWorked),
        averageComplexity: toNumber(formData.averageComplexity),
        productivityScore: toNumber(formData.productivityScore),
        codeQualityScore: toNumber(formData.codeQualityScore),
        validationScore: toNumber(formData.validationScore),
      });
      setAlert({
        isOpen: true,
        message: 'Feedback criado com sucesso!',
        type: 'success',
      });
      setTimeout(() => {
        navigate('/feedbacks');
      }, 1500);
    } catch (error: any) {
      setAlert({
        isOpen: true,
        message: error.response?.data?.message || 'Erro ao criar feedback',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageContainer
      icon={<RateReviewOutlinedIcon />}
      title="Criar feedback"
      subtitle="Registre a avaliação do desenvolvedor no ciclo: produtividade, qualidade de código, validação e um consolidado com pontos fortes, melhorias e próximos passos. Só são listados ciclos ativos cujo prazo de envio ainda não passou (em geral 3 dias corridos após o fim do período); para reabrir, um administrador pode alterar o prazo limite do ciclo."
    >
      <Box component="form" onSubmit={handleSubmit}>
        <Stack spacing={{ xs: 2, md: 3 }}>
          {/* Contexto — Informações básicas */}
          <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
            <Stack spacing={2}>
              <Box>
                <Typography variant="overline" color="text.secondary">
                  Contexto
                </Typography>
                <Typography variant="h6" component="h2">
                  Informações básicas
                </Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <SearchableSelect
                    label="Desenvolvedor"
                    fieldName="developerId"
                    options={developers}
                    value={formData.developerId}
                    onChange={handleChange}
                    placeholder="Selecione o desenvolvedor..."
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <SearchableSelect
                    label="Ciclo"
                    fieldName="cycleId"
                    options={cycles.map((c) => ({ id: c.id, name: c.name }))}
                    value={formData.cycleId}
                    onChange={handleChange}
                    placeholder="Selecione o ciclo..."
                    required
                    showSearch={false}
                  />
                </Grid>
              </Grid>
            </Stack>
          </Paper>

          {/* Produtividade técnica */}
          <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
            <Stack spacing={2}>
              <Box>
                <Typography variant="overline" color="text.secondary">
                  Produtividade
                </Typography>
                <Typography variant="h6" component="h2">
                  Produtividade técnica
                </Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Telas Desenvolvidas"
                    name="screensDeveloped"
                    value={formData.screensDeveloped}
                    onChange={handleChange}
                    slotProps={{ htmlInput: { min: 0 } }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Tarefas Concluídas"
                    name="tasksCompleted"
                    value={formData.tasksCompleted}
                    onChange={handleChange}
                    slotProps={{ htmlInput: { min: 0 } }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Horas Trabalhadas"
                    name="hoursWorked"
                    value={formData.hoursWorked}
                    onChange={handleChange}
                    slotProps={{ htmlInput: { min: 0, step: 0.5 } }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Complexidade Média (1-10)"
                    name="averageComplexity"
                    value={formData.averageComplexity}
                    onChange={handleChange}
                    slotProps={{ htmlInput: { min: 1, max: 10 } }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Score de Produtividade (1-5)"
                    name="productivityScore"
                    value={formData.productivityScore}
                    onChange={handleChange}
                    required
                    slotProps={{ htmlInput: { min: 1, max: 5 } }}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    label="Justificativa"
                    name="productivityJustification"
                    value={formData.productivityJustification}
                    onChange={handleChange}
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    label="Observações"
                    name="productivityObservations"
                    value={formData.productivityObservations}
                    onChange={handleChange}
                  />
                </Grid>
              </Grid>
            </Stack>
          </Paper>

          {/* Qualidade do código */}
          <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
            <Stack spacing={2}>
              <Box>
                <Typography variant="overline" color="text.secondary">
                  Código
                </Typography>
                <Typography variant="h6" component="h2">
                  Qualidade do código
                </Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Score de Qualidade (1-5)"
                    name="codeQualityScore"
                    value={formData.codeQualityScore}
                    onChange={handleChange}
                    required
                    slotProps={{ htmlInput: { min: 1, max: 5 } }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <PremiumSelect
                    label="Aderência aos Padrões"
                    value={formData.codeAdherence}
                    options={yesPartialNoOptions}
                    onChange={(value) =>
                      handleChange({ target: { name: 'codeAdherence', value } })
                    }
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    label="Observações"
                    name="codeQualityObservations"
                    value={formData.codeQualityObservations}
                    onChange={handleChange}
                  />
                </Grid>
              </Grid>
            </Stack>
          </Paper>

          {/* Validação e testes */}
          <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
            <Stack spacing={2}>
              <Box>
                <Typography variant="overline" color="text.secondary">
                  Qualidade
                </Typography>
                <Typography variant="h6" component="h2">
                  Validação e testes
                </Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Score de Validação (1-5)"
                    name="validationScore"
                    value={formData.validationScore}
                    onChange={handleChange}
                    required
                    slotProps={{ htmlInput: { min: 1, max: 5 } }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <PremiumSelect
                    label="Completude dos Testes"
                    value={formData.validationCompleteness}
                    options={yesPartialNoOptions}
                    onChange={(value) =>
                      handleChange({ target: { name: 'validationCompleteness', value } })
                    }
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    label="Observações"
                    name="validationObservations"
                    value={formData.validationObservations}
                    onChange={handleChange}
                  />
                </Grid>
              </Grid>
            </Stack>
          </Paper>

          {/* Resumo final */}
          <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
            <Stack spacing={2}>
              <Box>
                <Typography variant="overline" color="text.secondary">
                  Consolidado
                </Typography>
                <Typography variant="h6" component="h2">
                  Resumo final
                </Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    label="Pontos Fortes"
                    name="strengths"
                    value={formData.strengths}
                    onChange={handleChange}
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    label="Pontos de Melhoria"
                    name="improvements"
                    value={formData.improvements}
                    onChange={handleChange}
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    label="Ações para o Próximo Ciclo"
                    name="nextActions"
                    value={formData.nextActions}
                    onChange={handleChange}
                    required
                  />
                </Grid>
              </Grid>
            </Stack>
          </Paper>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            sx={{ justifyContent: 'flex-end' }}
          >
            <Button
              type="button"
              variant="outlined"
              startIcon={<CloseIcon />}
              onClick={() => navigate('/feedbacks')}
            >
              Cancelar
            </Button>
            <Button type="submit" variant="contained" disabled={loading} startIcon={<SaveIcon />}>
              {loading ? 'Salvando...' : 'Salvar feedback'}
            </Button>
          </Stack>
        </Stack>
      </Box>

      <AlertModal
        isOpen={alert.isOpen}
        onClose={() => setAlert({ ...alert, isOpen: false })}
        message={alert.message}
        type={alert.type}
      />
    </PageContainer>
  );
};

export default CreateFeedback;
